import { memo, useEffect, useMemo } from "react";

import { useRect } from "~/hooks/useRect";

import { SheetModal } from "../sheetModal/SheetModal";

import type { CSSProperties, ReactNode } from "react";
import type { WatchViewLayoutEvent } from "./WatchView";

export interface WatchViewDrawerProps {
  children?: ReactNode;
  isOpen?: boolean;
  isPrimary?: boolean;
  onLayout?: (event: WatchViewLayoutEvent) => void;
  onRequestClose?: () => void;
  isDesktopLayout?: boolean;
}

export const WatchViewDrawer = memo(function WatchViewDrawer({
  children,
  isOpen,
  isPrimary,
  onLayout,
  onRequestClose,
  isDesktopLayout,
}: WatchViewDrawerProps) {
  const [ref, DOMRect] = useRect<HTMLDivElement>();

  const style = useMemo(() => {
    const x: CSSProperties = {};

    if (!isDesktopLayout) {
      x.zIndex = isPrimary ? "var(--z-90)" : "var(--z-60)";
    }

    return x;
  }, [isDesktopLayout, isPrimary]);

  const sheetCustomProperties = {
    "--sheet-modal-inset-bottom": !isPrimary
      ? "var(--watchview-main-inset-bottom)"
      : undefined,
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      if (ref.current) {
        onLayout?.({ DOMRect, element: ref.current });
      }
    });
  }, [DOMRect, onLayout, ref]);

  return (
    <div
      ref={ref}
      className="WatchViewDrawer"
      aria-live="polite"
      aria-hidden={!isOpen}
      style={style}
    >
      {isDesktopLayout ? (
        children
      ) : (
        <SheetModal
          isOpen={isOpen}
          style={sheetCustomProperties}
          disableGestures
          header={null}
          root={ref.current}
          size="lg"
          onRequestClose={onRequestClose}
        >
          {children}
        </SheetModal>
      )}
    </div>
  );
});
